import { useSelector } from "react-redux";
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { menuRoutes } from "../../../router/menu";
import { RootState } from "../../../redux/store";
import { ProviderInvoiceService } from "../../../services/providerInvoices/providerInvoiceService";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { ProviderInvoicesApiResponse } from "../../../type/entities/provider-invoice-type";
import { toast } from "sonner";
import { useCallback, useEffect } from "react";
import { setSelectedView } from "../../../redux/statesSlice";
import CustomSpinner from "../../../loader/CustomSpinner";
import PageHeader from "../../../layout/shared/page-header";
import useFetch from "../../../hooks/useFetch";
import { ActionIcon, Button, Switch, Tooltip } from "rizzui";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { HiOutlineDownload } from "react-icons/hi";

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.providersInvoices.profile.info,
        permission: {
            group: 'provider_invoicing',
            action: 'get',
        },
    }
]

export default function ProviderInvoiceProfileLayout({ isLoading }: { isLoading?: boolean }) {
    
    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.providerInvoiceProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();
    const service = new ProviderInvoiceService();

    // DATA FETCHING

    
    //--------------------------------------------------------------------------------------------------------------
    /**
     *  @EN FETCHES PROVIDER INVOICE DATA BY ID.
     *  @ES OBTIENE LOS DATOS DE UNA FACTURA DE PROVEEDOR POR SU ID.
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loadingProviderInvoice, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getProviderInvoiceById(id);
        return response.getResponseData() as ProviderInvoicesApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    const pageHeader = {
        title: `Factura N° ${data?.number || 'Factura del Proveedor'}`,
        breadcrumb: [
            { name: 'Gestión de facturas', href: menuRoutes.providersInvoices.path },
            { name: 'Detalles de la factura' },
        ],
    };

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     *  @EN HANDLES THE DELETION OF A PROVIDER INVOICE BY ITS ID.
     *  @ES MUESTRA UNA CONFIRMACIÓN PARA ELIMINAR UNA FACTURA DE PROVEEDOR POR SU ID.
     */
    //--------------------------------------------------------------------------------------------------------------    
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteProviderInvoice(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.providersInvoices.path);
                toast.success('Factura eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLEA EL ESTADO DE PAGO DE UNA FACTURA DE PROVEEDOR
     * @EN TOGGLES THE PAID STATUS OF A PROVIDER INVOICE
     */
    // -------------------------------------------------------------------------------------------------------------
    const togglePaid = async (id: string) => {
        try {
            const response = (await service.togglePaidStatus(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGAR UNA FACTURA PROVEEDOR
     * @EN DOWNLOAD A PROVIDER INVOICE
     * 
     * @param id 
     * @returns 
     */
    // -------------------------------------------------------------------------------------------------------------
    const downloadInvoice = async (id: string, name: string) => {
        try {
            const response = await service.downloadProviderInvoice(id);
            if (response && response.getResponseData()) {
                const fileData = response.getResponseData();
                if (fileData) {
                    const blob = new Blob([fileData], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    const fileName = `factura_proveedor_${name}.pdf`;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success('Factura descargada correctamente en formato PDF');
                } else {
                    toast.error('No se pudieron obtener los archivos de facturas');
                }
            } else {
                toast.error('Error al descargar las facturas');
            }
        } catch (error: any) {
            toast.error(`Error al descargar las facturas: ${error.message || 'Error desconocido'}`);
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        navigate(`${menuRoutes.providersInvoices.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    <Tooltip
                        size="sm"
                        content={data?.isPaid ? 'Pagada' : 'No pagada'}
                        placement="top"
                        color="invert"
                    >
                        <Switch
                            id={data?.id}
                            label="Pagada"
                            labelPlacement="left"
                            checked={data?.isPaid}
                            onChange={() => togglePaid(data?.id)}
                        />
                    </Tooltip>
                    <div className="mt-1">
                        <Tooltip
                            size="sm"
                            content={'Descargar'}
                            placement="top"
                            color="invert"
                        >
                            <ActionIcon
                                onClick={() => downloadInvoice(data?.id || '', data?.number || '')}
                                as="span"
                                size="sm"
                                variant="outline"
                                className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                            >
                                <HiOutlineDownload className="h-4 w-4" size={20} />
                            </ActionIcon>
                        </Tooltip>
                    </div>
                    <Tooltip
                        size="sm"
                        content={'Eliminar'}
                        placement="top"
                        color="invert"
                    >
                        <div className="mt-1">
                            <DeletePopover
                                title={`Eliminar factura`}
                                description={`¿Estás seguro de que deseas eliminar la factura ${data?.number}?`}
                                onDelete={() => handleDelete(data?.id || '')}
                            />
                        </div>
                    </Tooltip>
                    
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <div className='m-auto h-full'><CustomSpinner /></div> : <Outlet/>}
        </>
    )
}